import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { createPayHereOrder, submitPayHereCheckout } from '../lib/payhere';
import { X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import type { RegistryItem, Registry } from '../types/registry';
import TransactionBreakdown from './TransactionBreakdown';

interface Props {
  item: RegistryItem;
  registry: Registry;
  onClose: () => void;
  onContribute: () => void;
}

export default function GroupGiftModal({ item, registry, onClose, onContribute }: Props) {
  const [contribution, setContribution] = useState({
    name: '',
    email: '',
    amount: 0,
    message: '',
    phone: '',
    address: '',
    city: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [guestUserId, setGuestUserId] = useState<string | null>(null);
  const [feeSettings, setFeeSettings] = useState({
    platformFee: 0,
    gatewayFee: 0
  });

  const safeTarget = item.target_amount && Number(item.target_amount) > 0 ? Number(item.target_amount) : 0;
  const collectedAmount = Number(item.collected_amount || 0);
  const remainingAmount = safeTarget > 0 ? Math.max(safeTarget - collectedAmount, 0) : 0;
  const progressPercentage = safeTarget > 0 ? Math.min((collectedAmount / safeTarget) * 100, 100) : 0;
  const isFullyFunded = safeTarget > 0 && collectedAmount >= safeTarget;
  const { user } = useAuth();
  const isOwner = user?.id === registry.user_id;

  useEffect(() => {
    loadFeeSettings();
  }, []);

  const loadFeeSettings = async () => {
    try {
      const { data: platformFeeData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'platform_fee_rate')
        .maybeSingle();

      const { data: gatewayFeeData } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'gateway_fee_rate')
        .maybeSingle();

      setFeeSettings({
        platformFee: parseFloat(platformFeeData?.value || '0'),
        gatewayFee: parseFloat(gatewayFeeData?.value || '0')
      });
    } catch (error) {
      console.error('Error loading fee settings:', error);
    }
  };

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contribution.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!contribution.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!contribution.phone.trim()) {
      setError('Phone number is required');
      return;
    }
    if (!contribution.address.trim()) {
      setError('Address is required');
      return;
    }
    if (!contribution.city.trim()) {
      setError('City is required');
      return;
    }
    if (contribution.amount <= 0 || contribution.amount > remainingAmount) {
      setError('Please enter a valid contribution amount');
      return;
    }
    setError('');
    setLoading(true);

    try {
      // Create guest user first
      const { data: guestUser, error: guestError } = await supabase
        .from('guest_users')
        .insert([{
          name: contribution.name.trim(),
          email: contribution.email.trim()
        }])
        .select()
        .single();

      if (guestError) throw guestError;
      if (!guestUser) throw new Error('Failed to create guest user');

      const nameParts = contribution.name.trim().split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(' ') || '';

      const response = await createPayHereOrder({
        itemId: item.id,
        itemName: item.name,
        originalAmount: contribution.amount,
        currency: registry.currency,
        feeResponsibility: registry.fee_responsibility,
        platformFeePercentage: feeSettings.platformFee,
        gatewayFeePercentage: feeSettings.gatewayFee,
        registryId: item.registry_id,
        registryOwnerId: registry.user_id,
        payerFirstName: firstName,
        payerLastName: lastName,
        payerEmail: contribution.email,
        payerPhone: contribution.phone,
        payerAddress: contribution.address,
        payerCity: contribution.city,
        guestId: guestUser.id,
        contributionMessage: contribution.message || undefined,
        isGroupGift: true,
      });

      submitPayHereCheckout({
        merchantId: response.merchantId,
        orderId: response.orderId,
        hash: response.hash,
        itemName: item.name,
        finalAmount: response.finalAmount,
        currency: registry.currency,
        firstName: firstName,
        lastName: lastName,
        email: contribution.email,
        phone: contribution.phone,
        address: contribution.address,
        city: contribution.city,
        registryId: item.registry_id,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/20 z-[100] flex flex-col justify-end sm:items-center sm:justify-center sm:p-4">
      <div className="absolute inset-0" onClick={onClose} />

      <div className="relative w-full sm:max-w-2xl lg:max-w-4xl bg-white sm:bg-white/80 sm:backdrop-blur-xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[92vh] sm:max-h-[90vh] sm:border sm:border-white/40 sm:shadow-soft-lg">

        <div className="flex justify-center pt-3 pb-1 sm:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-400" />
        </div>

        <div className="flex items-start justify-between px-5 pt-4 pb-4 sm:px-6 sm:pt-5 border-b border-white/40 flex-shrink-0">
          <div className="flex-1 min-w-0 pr-3">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-800 leading-snug font-poppins">Group Gift Contribution</h3>
            <p className="mt-1 text-sm text-gray-500 truncate font-poppins">Contributing to: {item.name}</p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-2 rounded-full text-gray-500 hover:text-gray-800 hover:bg-white/60 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
          {error && (
            <div className="mb-5 bg-red-50 text-red-700 p-4 rounded-lg text-sm font-poppins">
              {error}
            </div>
          )}

          <div className="bg-white/60 p-4 rounded-xl mb-5">
            <div className="flex justify-between text-sm text-gray-600 mb-2 font-poppins">
              <span>Progress: {progressPercentage.toFixed(1)}%</span>
              <span>{registry.currency} {collectedAmount.toFixed(2)} of {registry.currency} {safeTarget.toFixed(2)}</span>
            </div>
            <div className="h-2 bg-white/40 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <form onSubmit={handlePayment} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5 font-poppins">Your Name *</label>
                    <input
                      type="text"
                      value={contribution.name}
                      onChange={(e) => setContribution({ ...contribution, name: e.target.value })}
                      className="w-full px-3 py-2.5 bg-white/60 border border-white/40 text-gray-800 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 font-poppins"
                      placeholder="Full name"
                      required
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5 font-poppins">Email *</label>
                    <input
                      type="email"
                      value={contribution.email}
                      onChange={(e) => setContribution({ ...contribution, email: e.target.value })}
                      className="w-full px-3 py-2.5 bg-white/60 border border-white/40 text-gray-800 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 font-poppins"
                      placeholder="Email"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5 font-poppins">Phone Number *</label>
                  <input
                    type="tel"
                    value={contribution.phone}
                    onChange={(e) => setContribution({ ...contribution, phone: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/60 border border-white/40 text-gray-800 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 font-poppins"
                    placeholder="Enter your phone number"
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5 font-poppins">Address *</label>
                  <input
                    type="text"
                    value={contribution.address}
                    onChange={(e) => setContribution({ ...contribution, address: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/60 border border-white/40 text-gray-800 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 font-poppins"
                    placeholder="Enter your address"
                    required
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5 font-poppins">City *</label>
                  <input
                    type="text"
                    value={contribution.city}
                    onChange={(e) => setContribution({ ...contribution, city: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/60 border border-white/40 text-gray-800 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 font-poppins"
                    placeholder="Enter your city"
                    required
                    disabled={loading}
                  />
                </div>

                {!isFullyFunded && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1.5 font-poppins">
                      Contribution Amount ({registry.currency}) *
                    </label>
                    <input
                      type="number"
                      value={contribution.amount || ''}
                      onChange={(e) => {
                        const newAmount = Math.min(Number(e.target.value), remainingAmount);
                        setContribution({ ...contribution, amount: newAmount || 0 });
                      }}
                      className="w-full px-3 py-2.5 bg-white/60 border border-white/40 text-gray-800 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 font-poppins"
                      placeholder="Enter your contribution amount"
                      required
                      min={1}
                      max={remainingAmount}
                      step="0.01"
                      disabled={loading || remainingAmount <= 0}
                    />
                    {isOwner && (
                      <p className="text-xs text-gray-500 mt-1 font-poppins">
                        Maximum: {registry.currency} {remainingAmount.toFixed(2)} remaining
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5 font-poppins">Message (optional)</label>
                  <textarea
                    value={contribution.message}
                    onChange={(e) => setContribution({ ...contribution, message: e.target.value })}
                    className="w-full px-3 py-2.5 bg-white/60 border border-white/40 text-gray-800 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 font-poppins"
                    rows={3}
                    placeholder="Add a personal message..."
                    disabled={loading}
                  />
                </div>

                <div className="lg:hidden pt-1">
                  <TransactionBreakdown
                    originalAmount={contribution.amount || 0}
                    currency={registry.currency}
                    showDetailed={false}
                    feeResponsibility={registry.fee_responsibility}
                    platformFeePercentage={feeSettings.platformFee}
                    gatewayFeePercentage={feeSettings.gatewayFee}
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="flex-1 px-4 py-3 text-sm text-gray-600 hover:text-gray-800 bg-white/60 rounded-lg hover:bg-white/80 transition-colors font-poppins"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || contribution.amount <= 0 || contribution.amount > remainingAmount || remainingAmount <= 0}
                    className="flex-1 px-4 py-3 text-sm bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors font-medium font-poppins"
                  >
                    {loading ? 'Redirecting to PayHere...' : remainingAmount <= 0 ? 'Fully Funded' : 'Contribute via PayHere'}
                  </button>
                </div>
              </form>
            </div>

            <div className="hidden lg:block space-y-4">
              <TransactionBreakdown
                originalAmount={contribution.amount || 0}
                currency={registry.currency}
                showDetailed={false}
                feeResponsibility={registry.fee_responsibility}
                platformFeePercentage={feeSettings.platformFee}
                gatewayFeePercentage={feeSettings.gatewayFee}
              />
              <div className="bg-white/40 p-3 rounded-lg">
                <p className="text-xs text-gray-500 text-center font-poppins">
                  Your contribution is secure and processed by PayHere
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}